import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Share2, Star, Users, Award, ArrowRight, Instagram, Youtube, Twitter, MapPin, BadgeCheck, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import ShareProfileDialog from "@/components/ShareProfileDialog";
import StarAvatarFallback from "@/components/StarAvatarFallback";

export default function StarPublicProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [star, setStar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);
  const [portfolio, setPortfolio] = useState([]);
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);

  useEffect(() => {
    try {
      const t = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
      setIsAuthed(!!t);
    } catch { /* noop */ }
  }, []);

  const fetchStar = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stars/public/${userId}`);
      setStar(res.data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchPortfolio = useCallback(async () => {
    setLoadingPortfolio(true);
    try {
      const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
      const res = await axios.get(`${API}/influencer/${userId}/portfolio`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setPortfolio(res.data?.items || []);
    } catch (e) {
      setPortfolio([]);
    } finally {
      setLoadingPortfolio(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchStar();
  }, [fetchStar]);

  useEffect(() => {
    if (star) fetchPortfolio();
  }, [star, fetchPortfolio]);

  const getFileUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/")) return `${API.replace("/api", "")}${url}`;
    return `${API}/files/${url}`;
  };

  const handleCollabClick = () => {
    if (isAuthed) {
      // Navigate to brand discover or open collab flow
      navigate(`/brand/discover?star=${userId}`);
    } else {
      // Save redirect and go to login
      try { sessionStorage.setItem("redirectAfterLogin", `/star/${userId}`); } catch { /* noop */ }
      navigate("/");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFE6F0] to-[#E6DFFF] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#7B2FBE]" />
      </div>
    );
  }

  if (notFound || !star) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFE6F0] to-[#E6DFFF] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-lg">
          <Sparkles className="w-12 h-12 text-[#7B2FBE] mx-auto mb-3" />
          <h2 className="text-xl font-outfit font-bold text-[#1E293B]">Star not found</h2>
          <p className="text-[#64748B] mt-2">This profile may have been removed or the link is incorrect.</p>
          <Button onClick={() => navigate("/")} className="mt-4 bg-gradient-to-r from-[#7B2FBE] to-[#FF69B4] text-white">
            Go to FoundStar
          </Button>
        </div>
      </div>
    );
  }

  const categoryLabel = star.primary_category || (star.categories && star.categories[0]) || "Creator";
  const photoUrl = star.photo_url ? getFileUrl(star.photo_url) : null;
  // Pretty-print category id like "actors" -> "Actors", "voice_artists" -> "Voice Artists"
  const prettyCategory = (c) => {
    if (!c) return "";
    return String(c).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  };
  // Precise location — prefer the composed `location` string returned by the API,
  // else build one from village / city / district / state.
  const preciseLocation =
    star.location ||
    [star.village, star.city, star.district, star.state].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-br from-[#7B2FBE] via-[#9B4FDE] to-[#FF69B4] text-white">
        <div className="max-w-4xl mx-auto px-5 pt-8 pb-20 relative">
          {/* Header */}
          <div className="flex items-center justify-between">
            <button onClick={() => navigate("/")} className="flex items-center gap-2 text-white/90 hover:text-white font-outfit">
              <Sparkles className="w-5 h-5" />
              <span className="font-bold">FoundStar</span>
            </button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShareOpen(true)}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              data-testid="public-share-btn"
            >
              <Share2 className="w-4 h-4 mr-1" /> Share
            </Button>
          </div>

          {/* Profile */}
          <div className="mt-8 flex flex-col items-center text-center">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={star.name}
                className="w-28 h-28 md:w-36 md:h-36 rounded-full object-cover border-4 border-white shadow-xl"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            ) : (
              <div className="w-28 h-28 md:w-36 md:h-36 rounded-full overflow-hidden border-4 border-white shadow-xl">
                <StarAvatarFallback iconClassName="w-14 h-14 md:w-16 md:h-16" />
              </div>
            )}

            <h1 className="mt-4 font-outfit font-bold text-2xl md:text-3xl flex items-center gap-2">
              {star.name}
              {star.is_verified && (
                <BadgeCheck className="w-6 h-6 text-sky-300" title="Pro Creator" />
              )}
              {star.is_pro_creator && (
                <span className="text-xs bg-yellow-400 text-black px-2 py-0.5 rounded-full font-bold">PRO</span>
              )}
            </h1>

            <p className="text-white/90 mt-1">{prettyCategory(categoryLabel)}</p>

            {preciseLocation && (
              <p className="flex items-center gap-1 text-white/80 text-sm mt-1">
                <MapPin className="w-3.5 h-3.5" /> {preciseLocation}
              </p>
            )}

            {/* Categories chips (when multiple) */}
            {star.categories && star.categories.length > 1 && (
              <div className="mt-3 flex flex-wrap gap-1.5 justify-center max-w-md">
                {star.categories.map((c) => (
                  <span
                    key={`cat-${c}`}
                    className="text-[11px] font-medium bg-white/20 text-white px-2.5 py-1 rounded-full backdrop-blur"
                  >
                    {prettyCategory(c)}
                  </span>
                ))}
              </div>
            )}

            {/* Niches chips */}
            {star.niches && star.niches.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5 justify-center max-w-md">
                {star.niches.map((n) => (
                  <span
                    key={`niche-${n}`}
                    className="text-[11px] font-medium bg-gradient-to-r from-pink-400/40 to-fuchsia-400/40 text-white px-2.5 py-1 rounded-full ring-1 ring-white/30 backdrop-blur"
                  >
                    #{n}
                  </span>
                ))}
              </div>
            )}

            {star.bio && (
              <p className="text-white/90 mt-3 max-w-xl text-sm md:text-base">{star.bio}</p>
            )}

            {/* Stats */}
            <div className="mt-5 flex gap-3 md:gap-4 flex-wrap justify-center">
              {star.stats?.followers > 0 && (
                <StatPill icon={<Users className="w-4 h-4" />} label="Followers" value={formatCount(star.stats.followers)} />
              )}
              {star.stats?.rating > 0 && (
                <StatPill icon={<Star className="w-4 h-4" />} label="Rating" value={star.stats.rating.toFixed(1)} />
              )}
              {star.stats?.completed_collabs > 0 && (
                <StatPill icon={<Award className="w-4 h-4" />} label="Collabs" value={star.stats.completed_collabs} />
              )}
            </div>

            {/* Socials */}
            {(star.socials?.instagram || star.socials?.youtube || star.socials?.twitter) && (
              <div className="mt-4 flex gap-3 justify-center">
                {star.socials.instagram && (
                  <a href={`https://instagram.com/${star.socials.instagram.replace("@", "")}`} target="_blank" rel="noopener noreferrer"
                     className="bg-white/15 hover:bg-white/30 p-2 rounded-full transition-colors">
                    <Instagram className="w-4 h-4" />
                  </a>
                )}
                {star.socials.youtube && (
                  <a href={`https://youtube.com/@${star.socials.youtube.replace("@", "")}`} target="_blank" rel="noopener noreferrer"
                     className="bg-white/15 hover:bg-white/30 p-2 rounded-full transition-colors">
                    <Youtube className="w-4 h-4" />
                  </a>
                )}
                {star.socials.twitter && (
                  <a href={`https://twitter.com/${star.socials.twitter.replace("@", "")}`} target="_blank" rel="noopener noreferrer"
                     className="bg-white/15 hover:bg-white/30 p-2 rounded-full transition-colors">
                    <Twitter className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Curved bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-8 bg-[#F8FAFC] rounded-t-[32px]" />
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 -mt-4 relative z-10">
        {/* Portfolio section */}
        <div className="bg-white rounded-2xl p-5 md:p-6 shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit font-semibold text-lg text-[#1E293B]">Portfolio</h2>
            {portfolio.length > 0 && (
              <span className="text-xs text-[#94A3B8] font-medium">{portfolio.length} item{portfolio.length === 1 ? "" : "s"}</span>
            )}
          </div>

          {loadingPortfolio ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-[#7B2FBE]" />
            </div>
          ) : portfolio.length === 0 ? (
            <div className="text-center py-8 text-[#94A3B8]">
              <p>This star hasn't uploaded portfolio items yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {portfolio.map((item, idx) => {
                const fileUrl = getFileUrl(item.url);
                // Defensive: detect video from URL extension even if backend says "image"
                const urlLower = (item.url || "").toLowerCase().split("?")[0];
                const isVideo = item.type === "video" || /\.(mp4|mov|webm|avi|mkv|m4v)$/i.test(urlLower);
                return (
                  <a
                    key={item.item_id || idx}
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group aspect-square rounded-xl overflow-hidden bg-[#F1F5F9] hover:opacity-95 transition-opacity relative block"
                  >
                    {isVideo ? (
                      <>
                        <video
                          src={fileUrl}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                          preload="metadata"
                          onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                          onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-10 h-10 bg-black/50 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      </>
                    ) : (
                      <img
                        src={fileUrl}
                        alt={item.title || "portfolio"}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          // Fallback: hide broken image, show placeholder
                          e.currentTarget.style.display = "none";
                          if (e.currentTarget.parentElement) {
                            e.currentTarget.parentElement.classList.add("bg-gradient-to-br", "from-[#FFE6F0]", "to-[#E6DFFF]");
                          }
                        }}
                      />
                    )}
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="mt-5 bg-gradient-to-r from-[#7B2FBE] to-[#FF69B4] rounded-2xl p-6 text-white text-center shadow-lg">
          <h3 className="font-outfit font-bold text-xl">Love {star.name.split(" ")[0]}'s work?</h3>
          <p className="text-white/90 text-sm mt-1">
            {isAuthed
              ? "Start a collaboration or book them for your next campaign."
              : "Sign in to hire and start a collaboration in seconds."}
          </p>
          <Button
            onClick={handleCollabClick}
            size="lg"
            className="mt-4 bg-white text-[#7B2FBE] hover:bg-white/90 font-semibold"
            data-testid="collab-cta-btn"
          >
            {isAuthed ? "Collaborate / Book Now" : "Sign in to Hire"} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Footer */}
        <div className="py-8 text-center">
          <button onClick={() => navigate("/")} className="text-sm text-[#64748B] hover:text-[#7B2FBE] font-outfit">
            <span className="font-bold">FoundStar</span> · Find · Book · Collaborate
          </button>
        </div>
      </div>

      {/* Share Dialog */}
      <ShareProfileDialog open={shareOpen} onClose={() => setShareOpen(false)} star={star} />
    </div>
  );
}

function StatPill({ icon, label, value }) {
  return (
    <div className="bg-white/15 backdrop-blur rounded-xl px-3 py-2 flex items-center gap-2">
      {icon}
      <div>
        <p className="text-white font-bold text-sm leading-none">{value}</p>
        <p className="text-white/70 text-[10px]">{label}</p>
      </div>
    </div>
  );
}

function formatCount(n) {
  if (!n) return "0";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}
